import PopUpNotification, {
    DELIVERY_TYPE_LABELS,
    DeliveryTypes,
    POP_UP_NOTIFICATION,
} from '@advanza/interfaces/PopUpNotification'
import {
    Select,
    SelectContent,
    SelectGroup,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@advanza/shadcn-ui/src/components/ui/select'
import { Table as TableInstance } from '@tanstack/react-table'
import {
    ARCHIVED_COLUMN_ID,
    FOLDER_COLUMN_ID,
    NO_FOLDER_FILTER_VALUE,
} from 'components/popUpNotifications/PopUpNotificationColumnsDef'
import { FIELDS, usePopUpNotificationFolders } from 'hooks/popUpnotificationsHooks'

const ALL_VALUE = 'all'
const ARCHIVED_VALUE = 'archived'
const NO_FOLDER_VALUE = 'no_folder'

interface Props {
    table: TableInstance<PopUpNotification>
}

/**
 * Status, delivery type and folder filters for the overview.
 *
 * Filtering is client-side: the list is already fetched in full for the selected country, archived
 * rows included, so no round trip is needed to switch between them.
 */
const PopUpNotificationsFilterBar = ({ table }: Props) => {
    const { folders } = usePopUpNotificationFolders()

    const archivedFilter = table.getColumn(ARCHIVED_COLUMN_ID)?.getFilterValue()
    const statusFilter = table.getColumn(FIELDS.STATUS)?.getFilterValue()

    const statusValue = (() => {
        if (archivedFilter === true) return ARCHIVED_VALUE
        if (statusFilter === undefined) return ALL_VALUE
        return String(statusFilter)
    })()

    const handleStatusChange = (value: string) => {
        const statusColumn = table.getColumn(FIELDS.STATUS)
        const archivedColumn = table.getColumn(ARCHIVED_COLUMN_ID)

        if (value === ARCHIVED_VALUE) {
            statusColumn?.setFilterValue(undefined)
            archivedColumn?.setFilterValue(true)
            return
        }

        archivedColumn?.setFilterValue(false)
        statusColumn?.setFilterValue(value === ALL_VALUE ? undefined : parseInt(value, 10))
    }

    const deliveryTypeValue =
        (table.getColumn(FIELDS.DELIVERY_TYPE)?.getFilterValue() as string) ?? ALL_VALUE

    const folderFilter = table.getColumn(FOLDER_COLUMN_ID)?.getFilterValue()
    const folderValue = (() => {
        if (folderFilter === undefined) return ALL_VALUE
        if (folderFilter === NO_FOLDER_FILTER_VALUE) return NO_FOLDER_VALUE
        return String(folderFilter)
    })()

    const handleFolderChange = (value: string) => {
        const column = table.getColumn(FOLDER_COLUMN_ID)

        if (value === ALL_VALUE) {
            column?.setFilterValue(undefined)
        } else if (value === NO_FOLDER_VALUE) {
            column?.setFilterValue(NO_FOLDER_FILTER_VALUE)
        } else {
            column?.setFilterValue(parseInt(value, 10))
        }
    }

    return (
        <>
            <Select value={statusValue} onValueChange={handleStatusChange}>
                <SelectTrigger className="w-44 border border-neutral-200 bg-white">
                    <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                    <SelectGroup>
                        <SelectItem value={ALL_VALUE}>All statuses</SelectItem>
                        <SelectItem value="1">{POP_UP_NOTIFICATION.STATUS.ACTIVE}</SelectItem>
                        <SelectItem value="0">{POP_UP_NOTIFICATION.STATUS.INACTIVE}</SelectItem>
                        <SelectItem value="2">{POP_UP_NOTIFICATION.STATUS.TRIGGERABLE}</SelectItem>
                        <SelectItem value={ARCHIVED_VALUE}>Archived</SelectItem>
                    </SelectGroup>
                </SelectContent>
            </Select>
            <Select
                value={deliveryTypeValue}
                onValueChange={(value) =>
                    table
                        .getColumn(FIELDS.DELIVERY_TYPE)
                        ?.setFilterValue(value === ALL_VALUE ? undefined : value)
                }>
                <SelectTrigger className="w-48 border border-neutral-200 bg-white">
                    <SelectValue placeholder="Delivery type" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                    <SelectGroup>
                        <SelectItem value={ALL_VALUE}>All delivery types</SelectItem>
                        {Object.values(DeliveryTypes).map((deliveryType) => (
                            <SelectItem key={deliveryType} value={deliveryType}>
                                {DELIVERY_TYPE_LABELS[deliveryType]}
                            </SelectItem>
                        ))}
                    </SelectGroup>
                </SelectContent>
            </Select>
            <Select value={folderValue} onValueChange={handleFolderChange}>
                <SelectTrigger className="w-44 border border-neutral-200 bg-white">
                    <SelectValue placeholder="Folder" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                    <SelectGroup>
                        <SelectItem value={ALL_VALUE}>All folders</SelectItem>
                        <SelectItem value={NO_FOLDER_VALUE}>No folder</SelectItem>
                        {folders.map((folder) => (
                            <SelectItem
                                key={folder.pop_up_notification_folder_id}
                                value={String(folder.pop_up_notification_folder_id)}>
                                {folder.name}
                            </SelectItem>
                        ))}
                    </SelectGroup>
                </SelectContent>
            </Select>
        </>
    )
}

export default PopUpNotificationsFilterBar
